<template>
  <!-- Builds a drop-down: options in order, each with a title, what it counts as, a colour and an icon (US 9398).
       Fixed lists (Yes / No) keep their options; only what they count as, their colour and their icon change. -->
  <div class="op-builder">
    <div v-if="presets.length && !disabled && !fixed" class="op-builder__presets">
      <span class="us-muted">{{ $t('dropdown_options.start_from') }}</span>
      <button v-for="preset in presets"
              :key="preset"
              type="button"
              class="op-preset"
              :qid="'option-preset-' + preset"
              @click="usePreset(preset)"
      >
        {{ $t('dropdown_options.presets.' + preset + '.label') }}
      </button>
      <button v-if="copyFrom && copyFrom.length"
              type="button"
              class="op-preset"
              qid="option-preset-copy"
              @click="emitOptions(copyFrom.map(option => Object.assign({}, option)))"
      >
        {{ copyFromLabel }}
      </button>
    </div>

    <draggable :value="value"
               class="op-builder__list"
               handle=".op-row__grip"
               :disabled="disabled || fixed"
               :options="{ animation: 150 }"
               @input="emitOptions"
    >
      <div v-for="(option, index) in value" :key="index" class="op-row" qid="option-row">
        <span class="op-row__grip" :class="{ 'is-off': disabled || fixed }"><font-awesome-icon icon="grip-vertical"/></span>

        <div class="op-style">
          <button type="button"
                  class="op-style__button"
                  :class="{ 'is-open': openIndex === index }"
                  :style="{ '--c': styleOf(option).hex, '--t': styleOf(option).text }"
                  :disabled="disabled"
                  :aria-label="$t('dropdown_options.style')"
                  :aria-expanded="openIndex === index ? 'true' : 'false'"
                  qid="option-style-button"
                  @click="toggle(index)"
          >
            <font-awesome-icon :icon="styleOf(option).icon"/>
          </button>

          <div v-if="openIndex === index" class="op-picker" qid="option-style-picker">
            <div class="op-picker__label">{{ $t('dropdown_options.colour') }}</div>
            <div class="op-picker__colors">
              <button v-for="(hex, color) in colors"
                      :key="color"
                      type="button"
                      class="op-picker__color"
                      :class="{ 'is-on': styleOf(option).color === color }"
                      :style="{ background: hex }"
                      :aria-label="color"
                      :qid="'option-color-' + color"
                      @click="update(index, { color: color })"
              ></button>
            </div>
            <div class="op-picker__label">{{ $t('dropdown_options.icon') }}</div>
            <div class="op-picker__icons">
              <button v-for="icon in icons"
                      :key="icon"
                      type="button"
                      class="op-picker__icon"
                      :class="{ 'is-on': styleOf(option).icon === icon }"
                      :style="{ '--c': styleOf(option).hex, '--t': styleOf(option).text }"
                      :aria-label="icon"
                      :qid="'option-icon-' + icon"
                      @click="update(index, { icon: icon })"
              >
                <font-awesome-icon :icon="icon"/>
              </button>
            </div>
            <div class="op-picker__foot">
              <OptionPill :option="option"></OptionPill>
              <span class="us-grow"></span>
              <b-button size="sm" variant="secondary" qid="option-style-done" @click="openIndex = null">{{ $t('dropdown_options.done') }}</b-button>
            </div>
          </div>
        </div>

        <span v-if="fixed" class="op-row__title op-row__title--fixed">{{ fixedLabel(option.title) }}</span>
        <input v-else
               class="op-row__title"
               :value="option.title"
               :disabled="disabled"
               maxlength="255"
               :aria-label="$t('dropdown_options.title')"
               qid="option-title-input"
               @input="update(index, { title: $event.target.value })"
        />

        <label class="op-row__value">
          <span>{{ $t('dropdown_options.counts_as') }}</span>
          <select :value="valueOf(option)" :disabled="disabled" qid="option-value-select" @change="setValue(index, $event.target.value)">
            <optgroup v-for="group in valueGroups" :key="group.label" :label="$t(group.label)">
              <option v-for="value in group.values" :key="value" :value="value">{{ valueLabel(value) }}</option>
            </optgroup>
          </select>
        </label>

        <button v-if="!disabled && !fixed"
                type="button"
                class="us-icon-btn"
                :aria-label="$t('dropdown_options.remove')"
                qid="option-remove-button"
                @click="remove(index)"
        >
          <font-awesome-icon icon="times"/>
        </button>
      </div>
    </draggable>

    <div v-if="!disabled && !fixed" class="op-add">
      <font-awesome-icon icon="plus"/>
      <input v-model="newTitle"
             :placeholder="$t('dropdown_options.add_placeholder')"
             :aria-label="$t('dropdown_options.add_placeholder')"
             maxlength="255"
             qid="option-add-input"
             @keydown.enter.prevent="add"
      />
      <span class="home-kbd">↵</span>
    </div>
    <div v-if="duplicate" class="op-builder__error">{{ $t('dropdown_options.duplicate') }}</div>

    <div v-if="preview && value.length" class="op-builder__preview">
      <span class="op-builder__preview-label"><font-awesome-icon icon="eye"/>{{ $t('dropdown_options.preview') }}</span>
      <div class="op-pills">
        <OptionPill v-for="(option, index) in value" :key="index" :option="option"></OptionPill>
      </div>
    </div>
  </div>
</template>

<script>
  import draggable from 'vuedraggable';
  import OptionPill from '@/components/assessment/OptionPill.vue';
  import { OPTION_COLORS, OPTION_ICONS, OPTION_VALUE_GROUPS, OPTION_PRESETS, optionStyle, optionValue, makeOption } from '@/util/dropdownOptions.js';

  export default {
    name: 'OptionListBuilder',
    components: {
      draggable,
      OptionPill
    },
    props: {
      value: {
        type: Array,
        default: () => []
      },
      presets: {
        type: Array,
        default: () => []
      },
      copyFrom: {
        type: Array,
        default: null
      },
      copyFromLabel: {
        type: String,
        default: ''
      },
      preview: {
        type: Boolean,
        default: true
      },
      disabled: {
        type: Boolean,
        default: false
      },
      fixed: {
        type: Boolean,
        default: false
      }
    },
    data() {
      return {
        colors: OPTION_COLORS,
        icons: OPTION_ICONS,
        valueGroups: OPTION_VALUE_GROUPS,
        openIndex: null,
        newTitle: '',
        duplicate: false
      }
    },
    methods: {
      styleOf(option) {
        return optionStyle(option);
      },
      valueOf(option) {
        return optionValue(option);
      },
      // Yes and No are stored in English; show them in the user's language
      fixedLabel(title) {
        let key = 'dropdown_options.presets.yes_no.' + String(title).toLowerCase();
        return this.$te(key) ? this.$t(key) : title;
      },
      valueLabel(value) {
        return this.$t('maintenance.questions.answer_type.' + value.toLowerCase().replace(' ', '_'));
      },
      emitOptions(options) {
        this.$emit('input', options);
      },
      toggle(index) {
        this.openIndex = this.openIndex === index ? null : index;
      },
      update(index, changes) {
        let options = this.value.slice();
        // Save the shown style with the option, so it no longer follows what the option counts as
        let style = optionStyle(options[index]);
        options[index] = Object.assign({}, options[index], { color: style.color, icon: style.icon }, changes);
        this.emitOptions(options);
      },
      setValue(index, value) {
        let options = this.value.slice();
        options[index] = Object.assign({}, options[index], { value: { name: value, value: value } });
        this.emitOptions(options);
      },
      remove(index) {
        let options = this.value.slice();
        options.splice(index, 1);
        this.openIndex = null;
        this.emitOptions(options);
      },
      add() {
        let title = this.newTitle.trim();
        if (!title) {
          return;
        }
        this.duplicate = this.value.some(option => option.title.trim().toLowerCase() === title.toLowerCase());
        if (this.duplicate) {
          return;
        }
        this.emitOptions(this.value.concat([makeOption(title, 'Neutral')]));
        this.newTitle = '';
      },
      usePreset(preset) {
        this.openIndex = null;
        this.emitOptions(OPTION_PRESETS[preset].map(([label, value, color, icon]) => makeOption(this.$t(label), value, color, icon)));
      },
      // Clicks outside the open picker close it
      closePicker(event) {
        if (this.openIndex !== null && !event.target.closest('.op-style')) {
          this.openIndex = null;
        }
      }
    },
    mounted() {
      document.addEventListener('mousedown', this.closePicker);
    },
    beforeDestroy() {
      document.removeEventListener('mousedown', this.closePicker);
    }
  }
</script>
